import { Directive, ElementRef, HostListener, inject, Input, NgZone, OnDestroy } from '@angular/core';
import { debounceTime, filter, fromEvent, Subject, takeUntil } from 'rxjs';

/**
 * this directive is used on overlay elements
 * it will trap the focus in that element, so if the focus gets out of this element, (and the focus is NOT in an element with a higher
 * z-index than the element where the directive is on) it will bring back the focus to the first focussable element WITHIN the directives element
 *
 * It will also focus the first focussable element when the element with the directive is inited (unless you set [igA11yFocusFirst] to "false")
 *
 * you have to manually call `triggerShow()` and `triggerHide()` cause the automatic approach was too CPU intensive
 */

@Directive({
  standalone: true,
  selector: '[igA11yFocus]',
  exportAs: 'igA11yFocus'
})
export class A11yFocusDirective implements OnDestroy {
  #ngZone = inject(NgZone);
  #observer!: IntersectionObserver;


  /**
   * set to false if the first element should NOT be focussed on init
   */
  @Input() igA11yFocusFirst = true;

  /**
   * set to false if the last focusses element should NOT be re-focussed on destroy
   */
  @Input() igA11yFocusLast = true;

  readonly #el = inject(ElementRef);

  /**
   * the element focussed before our view got opened
   */
  lastFocussedElements: (Element | null)[] = [];
  #focusTimeout?: number;
  #shiftPressed = false;

  @HostListener('document:keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Shift') {
      this.#shiftPressed = true;
    }
  }
  @HostListener('document:keyup', ['$event']) onKeyUp(e: KeyboardEvent) {
    if (e.key === 'Shift') {
      this.#shiftPressed = false;
    }
  }

  #focusChanged$ = fromEvent(window, 'focus', { capture: true });
  #destroyed$ = new Subject<void>();
  #open = false;

  #focusableElements = [
    'input',
    'textarea',
    'select',
    '[tabindex]:not([tabindex="-1"])',
    'button',
    'a[href]:not(.ig-overlay-alert__close-btn):not(.ig-details-close-btn)'
  ]

  constructor() {
    this.#focusChanged$.pipe(
      takeUntil(this.#destroyed$),
      filter(() => !!this.#open),
      debounceTime(200)
    ).subscribe(() => {
      const myZIndex = parseInt(window.getComputedStyle(this.#el.nativeElement as HTMLElement)?.getPropertyValue('z-index'));

      if (document.activeElement && document.activeElement !== document.body) {
        let el: Element | null = document.activeElement;
        let contained = false;
        let maxZIndex = 0;

        while (el && !contained) {
          if (el === this.#el.nativeElement) {
            contained = true;
          }

          const zIndex = parseInt(window.getComputedStyle(el)?.getPropertyValue('z-index'));
          if (zIndex > maxZIndex) maxZIndex = zIndex;

          el = el.parentElement;
        }

        if (!contained && maxZIndex < myZIndex) {
          // the focus is outside our trap! get it back in and focus the first focusable element
          const focussable = this.#shiftPressed ? this.#getLastFocussable() : this.#getFirstFocussable();
          if (focussable) {
            focussable.focus();
          }
        }
      }

    })
  }

  ngOnDestroy(): void {
    this.#destroyed$.next();
    this.#destroyed$.complete();
    this.triggerHide();
  }



  /**
   * call this method after the dialog is shown
   */
  triggerShow() {
    this.#open = true;

    // save the current focussed element to focus it again after our view closed
    this.lastFocussedElements.push(document.activeElement);



    if (this.igA11yFocusFirst && !this.#focusInside()) {
      if (this.#focusTimeout) {
        window.clearTimeout(this.#focusTimeout);
      }
      this.#focusTimeout = window.setTimeout(() => {
        const firstFocusable = this.#getFirstFocussable();

        if (firstFocusable) {
          firstFocusable.focus();
        }
      }, 100);
    }



  }

  /**
   * call this method when the dialog is hidden
   */
  triggerHide() {
    this.#open = false;



    // re-focus the last focussed element
    if (this.igA11yFocusLast) {
      if (this.#focusTimeout) {
        window.clearTimeout(this.#focusTimeout);
      }
      this.#focusTimeout = window.setTimeout(() => {
        while (this.lastFocussedElements.length) {
          const el = this.lastFocussedElements.pop();

          if (el && document.body.contains(el)) {
            (el as HTMLElement).focus();
            this.lastFocussedElements = [];

          }
        }

      }, 100)
    }
  }

  #getFocussableElements(root: HTMLElement = this.#el.nativeElement) {
    return Array.from(
      root.querySelectorAll(
        this.#focusableElements.map(e => e + ':not(:disabled):not(.a11y-no-first-focus):not(.a11y-visible-hidden):not(.ig-tooltip-target)').join(', ')
      )
    ).filter(el => {
      return getComputedStyle(el).visibility !== 'hidden';
    });
  }

  #getFirstFocussable(): HTMLElement | null {
    const focussable = this.#getFocussableElements();
    if (focussable.length) {
      return focussable.at(0) as HTMLElement;
    }
    return null;
  }

  #getLastFocussable(): HTMLElement | null {
    const focussable = this.#getFocussableElements();
    if (focussable.length) {
      return focussable.at(focussable.length - 1) as HTMLElement;
    }
    return null;
  }

  #focusInside() {
    let el: Element | null = document.activeElement;
    let contained = false;

    while (el && !contained) {
      if (el === this.#el.nativeElement) {
        contained = true;
        break;
      }

      el = el.parentElement;
    }

    return contained;
  }
}
